import React, {useState, useRef, useEffect} from 'react'
import classNames from 'classnames'
import {fetchOk, fetchVenueByVenueIdServiceUtil, NetworkError} from '../../services'
import {useSelectedVenue} from '../../contexts/SelectedVenueContext'

import AvailableDaysRadioCards from '../../components/KegCollectionForm/AvailableDaysRadioCards'
import CollectionTimesSlider from '../../components/KegCollectionForm/CollectionTimesSlider'
import ErrorIcon from '../../images/svg/ErrorIcon'
import KegCollectionFormSkeleton from '../../components/KegCollectionFormSkeleton/KegCollectionFormSkeleton'
import KegLocationRadioCards from '../../components/KegCollectionForm/KegLocationRadioCards'
import KegQuantityInputs from '../../components/KegCollectionForm/KegQuantityInputs'
import LoadingSpinner from '../../images/svg/LoadingSpinner'
import PickupInstructionsTextarea from '../../components/KegCollectionForm/PickupInstructionsTextarea'
import PhoneInputComponent from '../../components/PhoneInput/PhoneInput'
import {useToast} from '../../contexts/ToastContext'
import {API_URL} from '../../constants'

import {
  convertCountryAlpha2CodeToAlpha3,
  getIsNumberOrStringRepresentationOfNumber,
  kegQuantityInputRegex,
} from '../../utils'

import {
  CurrentlyDisplayedPage,
  EmptyKegsForm,
  EmptyKegsFormKeysArray,
  PickupRequestBody,
  daysOfWeek,
  KegLocation,
} from '../../types'
import {FetchVenuesResponse as Address} from '../../services'
import type {CountryCode} from 'libphonenumber-js'

type DayOfWeek = keyof typeof daysOfWeek

const emptyKegsFormInitialValues: EmptyKegsForm = EmptyKegsFormKeysArray.reduce((acc, key) => {
  acc[key] = ''
  return acc
}, {} as EmptyKegsForm)

interface EnterQuantitiesPageProps {
  chosenLocation: Address | null
  setLastSubmissionDate: React.Dispatch<React.SetStateAction<Date | null>>
  setCurrentlyDisplayedPage: React.Dispatch<React.SetStateAction<CurrentlyDisplayedPage>>
  hasUserConsentedToCookies: boolean | null
  isLocationToQtysPageSwipeCurrentlyAnimating: boolean
}

export default function EnterQuantitiesPage({
  chosenLocation,
  setLastSubmissionDate,
  setCurrentlyDisplayedPage,
  hasUserConsentedToCookies,
  isLocationToQtysPageSwipeCurrentlyAnimating, // codeCommentsAutoFocusDistortsAnimation
}: EnterQuantitiesPageProps) {
  const {showToast} = useToast()
  const {selectedVenue, setSelectedVenue, selectedVenueCollectionInfo} = useSelectedVenue()

  const [formData, setFormData] = useState<EmptyKegsForm>(emptyKegsFormInitialValues)
  const [isLoadingVenue, setIsLoadingVenue] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitFailure, setSubmitFailure] = useState<'network' | 'server' | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  // Form values - begin
  const [pickupNotes, setPickupNotes] = useState('')
  const [kegLocation, setKegLocation] = useState<KegLocation>('Inside' as KegLocation)
  const [collectionDays, setCollectionDays] = useState<DayOfWeek[]>([])
  const [sliderTimeRange, setSliderTimeRange] = useState<number[]>([12, 18])
  const [phoneInputValue, setPhoneInputValue] = useState('')
  const [phoneInputCountry, setPhoneInputCountry] = useState<CountryCode | undefined>(
    (chosenLocation?.country as CountryCode) || undefined
  )
  // Form values - end

  const [flagFormErrors, setFlagFormErrors] = useState<boolean>(false)

  useEffect(() => {
    // This useEffect is used to fetch the venue collection information for the chosen location.
    // The values will be used to populate the keg collection request form.
    // Should the API call fail, we will not block the user from submitting the form.
    const abortController = new AbortController()

    const fetchVenue = async (customerId: string) => {
      setIsLoadingVenue(true)
      try {
        const venue = await fetchVenueByVenueIdServiceUtil(customerId, abortController.signal)
        if (abortController.signal.aborted) return
        setSelectedVenue(venue)
      } catch (error) {
        if (abortController.signal.aborted) return
        // This API call is not critical to the user experience,
        // so we can afford to fail silently if the API is weak or down.
        console.error('Error fetching venue: ', error)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingVenue(false)
        }
      }
    }

    if (chosenLocation && chosenLocation.customerId !== selectedVenue?.customerId) {
      fetchVenue(chosenLocation.customerId)
    }

    return () => {
      abortController.abort()
    }
  }, [setSelectedVenue, chosenLocation, selectedVenue])

  useEffect(() => {
    if (selectedVenue && selectedVenueCollectionInfo) {
      setPickupNotes(selectedVenueCollectionInfo.pickupInstructions || '')
      setKegLocation(selectedVenueCollectionInfo.pickupLocation || ('Inside' as KegLocation))
      setCollectionDays(selectedVenueCollectionInfo.collectionDays || [])
      setSliderTimeRange(selectedVenueCollectionInfo.timeRange || [12, 18])
      setPhoneInputValue(selectedVenueCollectionInfo.phoneNumber || '')
    }
  }, [selectedVenue, selectedVenueCollectionInfo])

  useEffect(() => {
    if (hasSubmitted && !submitFailure) {
      showToast({
        title: 'Success',
        description: 'Your keg collection request has been submitted.',
        variant: 'success',
        overlayMode: 'dim',
        autoDismiss: false,
      })
    } else if (submitFailure === 'network') {
      showToast({
        title: 'Connection Problem',
        description: "We couldn't reach the server. Check your internet connection and try again.",
        variant: 'warning',
        overlayMode: 'dim',
        autoDismiss: false,
      })
    } else if (submitFailure === 'server') {
      showToast({
        title: 'Error',
        description:
          'Your keg collection request submission has failed, please try again. If the issue persists, refresh the page or contact support.',
        variant: 'error',
        overlayMode: 'dim',
        autoDismiss: false,
      })
    }
  }, [submitFailure, hasSubmitted, showToast])

  // codeCommentsAutoFocusDistortsAnimation
  const firstFormInput = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!isLocationToQtysPageSwipeCurrentlyAnimating) {
      // codeCommentsOpenMobileVirtualKeyboardOnAutoFocus
      setTimeout(() => {
        firstFormInput.current?.blur()
        firstFormInput.current?.focus()
      }, 100)
    }
  }, [isLocationToQtysPageSwipeCurrentlyAnimating])

  function handleQuantityInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const {name, value} = e.target

    if (!getIsNumberOrStringRepresentationOfNumber(value)) return
    // The user isn't allowed to enter more than 3 digits because no restaurant
    // has >=999 empty kegs of a single size lying around.
    // Additionally, add final guard to prevent non-numeric input
    if (value.length > 3 || !kegQuantityInputRegex.test(value)) return

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  function handleOnPhoneInputChange(newValue: string) {
    setPhoneInputValue(newValue)
  }

  function handleAvailableDayClick(day: DayOfWeek) {
    setCollectionDays((prev) => {
      const isSelected = prev.includes(day as DayOfWeek)
      return isSelected ? prev.filter((d) => d !== (day as DayOfWeek)) : [...prev, day as DayOfWeek]
    })
  }

  const noKegQuantities = Object.values(formData).every((value) => value.length === 0)
  const noDaysSelected = collectionDays.length === 0
  const noPhone = phoneInputValue.length === 0

  const isFormMissingRequiredValues = noKegQuantities || noDaysSelected || noPhone

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()

    if (isFormMissingRequiredValues) {
      setFlagFormErrors(true)
      return
    }

    setFlagFormErrors(false)
    setIsSubmitting(true)
    if (submitFailure) setSubmitFailure(null)
    if (hasSubmitted) setHasSubmitted(false)

    const pickupRequestBody: PickupRequestBody = {
      customerId: chosenLocation!.customerId,
      addressId: chosenLocation!.addressId,
      venueOpenMonday: collectionDays.includes('Monday'),
      venueOpenTuesday: collectionDays.includes('Tuesday'),
      venueOpenWednesday: collectionDays.includes('Wednesday'),
      venueOpenThursday: collectionDays.includes('Thursday'),
      venueOpenFriday: collectionDays.includes('Friday'),
      startHour: sliderTimeRange[0],
      endHour: sliderTimeRange[1],
      kegLocation: kegLocation,
      quantity20L: formData['20L'] ? Number(formData['20L']) : 0,
      quantity30L: formData['30L'] ? Number(formData['30L']) : 0,
      quantity50L: formData['50L'] ? Number(formData['50L']) : 0,
      quantity9G: formData['9G'] ? Number(formData['9G']) : 0,
      pickupInstruction: pickupNotes.trim(),
      phoneNumber: phoneInputValue,
    }

    try {
      await fetchOk(`${API_URL}/requestPickup`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pickupRequestBody),
      })

      setHasSubmitted(true)
      setFormData(emptyKegsFormInitialValues)
      if (hasUserConsentedToCookies) {
        setLastSubmissionDate(new Date())
      }
    } catch (error) {
      console.error('Error:', error)
      setSubmitFailure(error instanceof NetworkError ? 'network' : 'server')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormLocked = isSubmitting

  const submitButtonClassName = classNames({
    'w-full font-semibold py-2 px-4 rounded focus:outline-none text-white': true,
    'bg-sky-500 hover:bg-sky-400 focus:bg-sky-400 dark:bg-sky-600 dark:hover:bg-sky-500 dark:focus:bg-sky-500':
      !isFormLocked,
    'bg-sky-300 dark:bg-sky-800': isFormLocked,
  })

  if (isLoadingVenue) {
    return <KegCollectionFormSkeleton />
  }

  return (
    <>
      <div className="break-words text-center">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          <span>{chosenLocation!.name}</span>
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">{chosenLocation!.address1}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {`${chosenLocation!.city}, ${chosenLocation!.stateOrProvince && chosenLocation!.stateOrProvince !== chosenLocation!.city ? chosenLocation!.stateOrProvince : ''} ${convertCountryAlpha2CodeToAlpha3(chosenLocation!.country)} ${chosenLocation!.postalCode}`}
        </p>
        <p
          className="text-xs pt-1 text-sky-500 dark:text-sky-400 hover:text-sky-400 dark:hover:text-sky-300 hover:cursor-pointer"
          onClick={() => setCurrentlyDisplayedPage(CurrentlyDisplayedPage.ChooseLocation)}
        >
          Choose different location
        </p>
      </div>

      <form className="px-7 py-9">
        <KegQuantityInputs
          formValues={formData}
          onChange={handleQuantityInputChange}
          isDisabled={isFormLocked}
          inputRef={firstFormInput}
          flagFormErrors={flagFormErrors}
          chosenLocation={chosenLocation}
        />

        <AvailableDaysRadioCards
          chosenLocation={chosenLocation}
          handleOnClick={handleAvailableDayClick}
          collectionDays={collectionDays}
          flagFormErrors={flagFormErrors}
          isDisabled={isFormLocked}
        />

        <CollectionTimesSlider
          sliderTimeRange={sliderTimeRange}
          onChange={(value) => setSliderTimeRange(value)}
          isDisabled={isFormLocked}
        />

        <KegLocationRadioCards
          onClick={(location: KegLocation) => {
            setKegLocation(location)
          }}
          kegLocation={kegLocation}
          isDisabled={isFormLocked}
        />

        <PhoneInputComponent
          phoneInputValue={phoneInputValue}
          onChangeCallback={handleOnPhoneInputChange}
          defaultRegion={phoneInputCountry}
          isDisabled={isFormLocked}
          onCountryChangeCallback={(value: CountryCode) => setPhoneInputCountry(value)}
          flagFormErrors={flagFormErrors}
        />

        <PickupInstructionsTextarea
          value={pickupNotes}
          onChange={setPickupNotes}
          isDisabled={isFormLocked}
        />

        {isFormMissingRequiredValues && flagFormErrors && (
          <div className="bg-red-100 dark:bg-red-950/30 border border-red-400 dark:border-red-500/60 text-red-700 dark:text-red-200 px-4 py-3 rounded relative mb-5 text-sm">
            <span className="flex items-center">
              <ErrorIcon className="inline-block h-5 w-5 mr-2 align-middle" />
              <strong className="font-bold">Fix the following errors to proceed:</strong>
            </span>
            <ul className="list-disc list-inside mt-2">
              {noKegQuantities && (
                <li>Must select at least one keg size with a quantity greater than zero.</li>
              )}
              {noDaysSelected && <li>Must select at least one collection day.</li>}
              {noPhone && <li>Contact phone number is required.</li>}
            </ul>
          </div>
        )}

        <div>
          <button
            className={submitButtonClassName}
            type="submit"
            disabled={isFormLocked}
            onClick={handleSubmit}
            tabIndex={0}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="small" /> <span>Submitting...</span>
              </span>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </form>
    </>
  )
}
